/**
 * Typed wrappers for the admin users CRUD endpoints.
 *
 *   GET    /api/v1/admin/users
 *   POST   /api/v1/admin/users
 *   PATCH  /api/v1/admin/users/:id
 *   DELETE /api/v1/admin/users/:id
 *
 * NOTE — at the time of Task 29 these endpoints DO NOT yet exist on the
 * server (verified by grep over `apps/server/src`). Task 29's constraints
 * forbid touching anything outside `apps/admin/src`, so the wrappers
 * point at the documented URLs and will 404 at runtime. The page
 * components surface load-errors gracefully; tests mock these wrappers.
 *
 * The shape of the response and request bodies mirrors `UserSummary` from
 * `@displaycms/contracts` (locale + roles + display_name + username),
 * with `password` accepted on create and on optional update.
 */

import type { Locale, Role, UserSummary } from '@displaycms/contracts';

import { apiDelete, apiGet, apiPatch, apiPost } from '@/api/client';

// The server's GET /admin/users returns a raw array of UserSummary —
// admin-users.controller.ts delegates straight to the service which
// returns `UserSummary[]`. Align the type with the actual wire shape;
// the legacy `{users: UserSummary[]}` envelope was a UI-side fiction
// that left `usersQuery.data?.users` permanently undefined and made
// the users page show an empty state even when the DB had rows.
export type UserListResponse = UserSummary[];

export function listUsers(
  options?: { signal?: AbortSignal },
): Promise<UserListResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<UserListResponse>('/v1/admin/users', opts);
}

export interface CreateUserInput {
  username: string;
  password: string;
  display_name: string;
  locale: Locale;
  roles: readonly Role[];
}

export function createUser(input: CreateUserInput): Promise<UserSummary> {
  return apiPost<UserSummary>('/v1/admin/users', input);
}

export interface UpdateUserInput {
  display_name?: string | undefined;
  locale?: Locale | undefined;
  roles?: readonly Role[] | undefined;
  password?: string | undefined;
}

export function updateUser(
  id: string,
  patch: UpdateUserInput,
): Promise<UserSummary> {
  return apiPatch<UserSummary>(`/v1/admin/users/${id}`, patch);
}

export function deleteUser(id: string): Promise<void> {
  return apiDelete<void>(`/v1/admin/users/${id}`);
}

export interface ImportCsvRowError {
  row: number;
  message: string;
}

export interface ImportCsvResult {
  created: number;
  skipped: number;
  errors: ImportCsvRowError[];
}

/**
 * POST /api/v1/admin/users/import-csv (multipart)
 *
 * Uploads a CSV file and receives an import summary.
 * Sends as multipart/form-data so we must NOT set Content-Type header
 * (browser sets it with the correct boundary automatically).
 */
export async function importUsersCsv(file: File): Promise<ImportCsvResult> {
  const formData = new FormData();
  formData.append('file', file);

  let response: Response;
  try {
    response = await fetch('/api/v1/admin/users/import-csv', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-Requested-With': 'displaycms-admin',
        // Do NOT set Content-Type — browser sets it with boundary
      },
      body: formData,
    });
  } catch (cause) {
    throw new Error(
      `network_error: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  const data = (await response.json()) as ImportCsvResult;
  if (!response.ok) {
    throw new Error(
      typeof (data as unknown as { message?: string }).message === 'string'
        ? (data as unknown as { message: string }).message
        : 'import_failed',
    );
  }
  return data;
}
